import React, { createContext, FC, ReactNode } from 'react'
import { useParams } from 'react-router-dom'

import { useGetRepairOrderMediaEntriesByApiKeyQuery } from '../../redux/api'
import { useGetCannedJobExpandedByApiKeyQuery } from '../../redux/api/cannedJobApi'
import { useGetApiKeyInventoryPartsQuery } from '../../redux/api/customerPortalApi'
import {
  useGetCustomerInfoByApiKeyQuery,
  useGetCustomerPortalInfoForCustomerQuery,
  useGetInspectionEntryServiceAssociationsByApiKeyQuery,
  useGetRepairOrderByApiKeyQuery,
  useGetServiceAdvisorForApiKeyQuery,
  useGetServicesByApiKeyQuery,
  useGetShopProfileForCustomersQuery,
  useGetTechnicianForApiKeyQuery,
  useGetVehicleImagesForApiKeyQuery,
  useGetVehicleInfoForCustomerQuery,
  useListAuthorizationsForApiKeyQuery
} from '../../redux/api/inspectionApi'
import {
  AuthorizationEntry,
  CannedJobExpanded,
  CustomerEntry,
  CustomerPortalInfo,
  InspectionEntryServiceEntry,
  InventoryPartEntry,
  RepairOrderExpanded,
  RepairOrderMediaEntry,
  ServiceExpanded,
  ShopEntry,
  UserProfile,
  VehicleEntry,
  VehicleImageEntry
} from '../../sdk'

interface RepairOrderPortalContextValues {
  repairOrder: RepairOrderExpanded | undefined
  services: ServiceExpanded[] | undefined
  customer: CustomerEntry | undefined
  vehicle: VehicleEntry | undefined
  shop: ShopEntry | undefined
  portalInfo: CustomerPortalInfo | undefined
  authorizations: AuthorizationEntry[] | undefined
  associations: InspectionEntryServiceEntry[] | undefined
  technician: UserProfile[] | undefined
  serviceAdvisor: UserProfile[] | undefined
  vehicleImages: VehicleImageEntry[] | undefined
  isLoading: boolean
  inspection: boolean
  cannedJobs: CannedJobExpanded[] | undefined
  media: RepairOrderMediaEntry[] | undefined
  serviceInventoryParts: InventoryPartEntry[] | undefined
}

interface Props {
  inspection: boolean
  children?: ReactNode
}

export const RepairOrderPortalContext =
  createContext<RepairOrderPortalContextValues>({
    repairOrder: undefined,
    services: undefined,
    customer: undefined,
    vehicle: undefined,
    shop: undefined,
    portalInfo: undefined,
    authorizations: undefined,
    associations: undefined,
    technician: undefined,
    serviceAdvisor: undefined,
    vehicleImages: undefined,
    isLoading: false,
    inspection: false,
    cannedJobs: undefined,
    media: undefined,
    serviceInventoryParts: undefined
  })

const RepairOrderPortalProvider: FC<Props> = ({ children, inspection }) => {
  const { apiKey } = useParams()
  const { data: repairOrder, isLoading: repairOrderLoading } =
    useGetRepairOrderByApiKeyQuery({
      xAPIKey: apiKey!
    })
  const { data: services, isLoading: servicesLoading } =
    useGetServicesByApiKeyQuery({
      xAPIKey: apiKey!
    })
  const { data: customer, isLoading: customerLoading } =
    useGetCustomerInfoByApiKeyQuery({
      xAPIKey: apiKey!
    })
  const { data: vehicle, isLoading: vehicleLoading } =
    useGetVehicleInfoForCustomerQuery({
      xAPIKey: apiKey!
    })
  const { data: shop, isLoading: shopLoading } =
    useGetShopProfileForCustomersQuery({
      xAPIKey: apiKey!
    })
  const { data: portalInfo, isLoading: portalInfoLoading } =
    useGetCustomerPortalInfoForCustomerQuery({
      xAPIKey: apiKey!
    })
  const { data: authorizations, isLoading: authorizationsLoading } =
    useListAuthorizationsForApiKeyQuery({
      xAPIKey: apiKey!
    })
  const { data: associations, isLoading: associationsLoading } =
    useGetInspectionEntryServiceAssociationsByApiKeyQuery({
      xAPIKey: apiKey!
    })
  const { data: technician, isLoading: technicianLoading } =
    useGetTechnicianForApiKeyQuery({
      xAPIKey: apiKey!
    })
  const { data: serviceAdvisor, isLoading: serviceAdvisorLoading } =
    useGetServiceAdvisorForApiKeyQuery({
      xAPIKey: apiKey!
    })
  const { data: vehicleImages, isLoading: vehicleImagesLoading } =
    useGetVehicleImagesForApiKeyQuery({
      xAPIKey: apiKey!
    })
  const { data: cannedJobs, isLoading: cannedJobsLoading } =
    useGetCannedJobExpandedByApiKeyQuery({
      xAPIKey: apiKey!
    })
  const { data: media, isLoading: mediaLoading } =
    useGetRepairOrderMediaEntriesByApiKeyQuery({
      xAPIKey: apiKey!
    })
  const {
    data: serviceInventoryParts,
    isLoading: serviceInventoryPartsLoading
  } = useGetApiKeyInventoryPartsQuery({
    xAPIKey: apiKey!
  })

  const isLoading =
    repairOrderLoading ||
    servicesLoading ||
    customerLoading ||
    vehicleLoading ||
    shopLoading ||
    portalInfoLoading ||
    authorizationsLoading ||
    associationsLoading ||
    technicianLoading ||
    serviceAdvisorLoading ||
    vehicleImagesLoading ||
    cannedJobsLoading ||
    mediaLoading ||
    serviceInventoryPartsLoading

  return (
    <RepairOrderPortalContext.Provider
      value={{
        repairOrder,
        services,
        customer,
        vehicle,
        shop,
        portalInfo,
        authorizations,
        associations,
        technician,
        serviceAdvisor,
        vehicleImages,
        isLoading,
        inspection,
        cannedJobs,
        media,
        serviceInventoryParts
      }}>
      {children}
    </RepairOrderPortalContext.Provider>
  )
}

export default RepairOrderPortalProvider
